import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { Modal } from "@/components/common";
import { Role, DEFAULT_ADMIN_ACCESS, DEFAULT_STAFF_ACCESS, } from "@/types/auth.types";
import styles from "./NewUserModal.module.css";
const ROLE_OPTIONS = [
    { value: Role.ADMIN, label: "Admin" },
    { value: Role.STAFF, label: "Staff" },
];
const getRoleLabel = (role) => {
    const option = ROLE_OPTIONS.find((r) => r.value === role);
    return option?.label || role;
};
const getRoleClassName = (role) => {
    switch (role) {
        case Role.ADMIN:
            return "admin";
        case Role.STAFF:
            return "staff";
        default:
            return "";
    }
};
const CORE_ACCESS_ITEMS = [
    { key: "contacts", label: "Contacts" },
    { key: "sites", label: "Sites" },
    { key: "estimating", label: "Estimating" },
    { key: "jobs", label: "Jobs" },
];
const ADDITIONAL_ACCESS_ITEMS = [
    { key: "all", label: "All" },
    { key: "templates", label: "Templates" },
    { key: "costSettings", label: "Cost Settings" },
    { key: "forms", label: "Forms" },
    { key: "userManagement", label: "User Management" },
];
const isCoreAccessKey = (key) => CORE_ACCESS_ITEMS.some((item) => item.key === key);
const STAFF_DEFAULT_ACCESS = {
    ...DEFAULT_STAFF_ACCESS,
    contacts: true,
    sites: true,
    estimating: true,
    jobs: true,
};
export const NewUserModal = ({ isOpen, onClose, onSave, isSaving = false, }) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [selectedRole, setSelectedRole] = useState(Role.ADMIN);
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [access, setAccess] = useState(DEFAULT_ADMIN_ACCESS);
    const dropdownRef = useRef(null);
    const resetFormState = useCallback(() => {
        setName("");
        setEmail("");
        setSelectedRole(Role.ADMIN);
        setIsRoleDropdownOpen(false);
        setAccess(DEFAULT_ADMIN_ACCESS);
    }, []);
    useEffect(() => {
        if (!isOpen) {
            resetFormState();
        }
    }, [isOpen, resetFormState]);
    const handleClose = useCallback(() => {
        if (isSaving)
            return;
        onClose();
    }, [isSaving, onClose]);
    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current &&
                !dropdownRef.current.contains(event.target)) {
                setIsRoleDropdownOpen(false);
            }
        };
        if (isRoleDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isRoleDropdownOpen]);
    const handleRoleChange = useCallback((newRole) => {
        setSelectedRole(newRole);
        if (newRole === Role.ADMIN) {
            setAccess(DEFAULT_ADMIN_ACCESS);
        }
        else {
            setAccess(STAFF_DEFAULT_ACCESS);
        }
        setIsRoleDropdownOpen(false);
    }, []);
    const isAdminRole = selectedRole === Role.ADMIN;
    const handleAccessToggle = useCallback((key) => {
        setAccess((prev) => {
            if (isAdminRole && isCoreAccessKey(key)) {
                return prev;
            }
            if (key === "all") {
                const newAllValue = !prev.all;
                const nextAccess = {
                    all: newAllValue,
                    sites: newAllValue,
                    quotes: newAllValue,
                    templates: newAllValue,
                    jobs: newAllValue,
                    contacts: newAllValue,
                    estimating: newAllValue,
                    costSettings: newAllValue,
                    forms: newAllValue,
                    userManagement: newAllValue,
                };
                if (isAdminRole) {
                    CORE_ACCESS_ITEMS.forEach((item) => {
                        nextAccess[item.key] = true;
                    });
                }
                return nextAccess;
            }
            const newAccess = { ...prev, [key]: !prev[key] };
            if (key === "quotes") {
                newAccess.estimating = newAccess.quotes;
            }
            else if (key === "estimating") {
                newAccess.quotes = newAccess.estimating;
            }
            if (isAdminRole) {
                CORE_ACCESS_ITEMS.forEach((item) => {
                    newAccess[item.key] = true;
                });
            }
            const allEnabled = newAccess.contacts &&
                newAccess.sites &&
                newAccess.estimating &&
                newAccess.jobs &&
                newAccess.quotes &&
                newAccess.templates &&
                newAccess.costSettings &&
                newAccess.forms &&
                newAccess.userManagement;
            newAccess.all = allEnabled;
            return newAccess;
        });
    }, [isAdminRole]);
    const handleSave = useCallback(() => {
        if (!name.trim() || !email.trim())
            return;
        const newUser = {
            id: `temp-${Date.now()}`,
            name: name.trim(),
            email: email.trim(),
            role: selectedRole,
            access: selectedRole === Role.STAFF ? access : undefined,
        };
        onSave(newUser);
    }, [name, email, selectedRole, access, onSave]);
    const canAddUser = name.trim() && email.trim();
    return (_jsx(Modal, { isOpen: isOpen, onClose: handleClose, children: _jsxs("div", { className: styles.modalWrapper, children: [_jsxs("div", { className: styles.modalHeader, children: [_jsx("h2", { className: styles.modalTitle, children: "New User" }), _jsx("p", { className: styles.modalDescription, children: "To add a user you must enter their name and email." })] }), _jsxs("div", { className: styles.formSection, children: [_jsxs("div", { className: styles.formRow, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: styles.formLabel, children: "Name" }), _jsx("input", { type: "text", className: styles.formInput, value: name, onChange: (e) => setName(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: styles.formLabel, children: "Email" }), _jsx("input", { type: "email", className: styles.formInput, value: email, onChange: (e) => setEmail(e.target.value) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: styles.formLabel, children: "Select Role" }), _jsxs("div", { ref: dropdownRef, className: styles.roleDropdownContainer, children: [_jsxs("button", { type: "button", className: `${styles.roleDropdownToggle} ${styles[getRoleClassName(selectedRole)] || ""}`, onClick: () => setIsRoleDropdownOpen(!isRoleDropdownOpen), children: [_jsx("span", { children: getRoleLabel(selectedRole) }), _jsx("svg", { className: `${styles.roleDropdownArrow} ${isRoleDropdownOpen ? styles.open : ""}`, width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", children: _jsx("path", { d: "M4 6L8 10L12 6", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })] }), isRoleDropdownOpen && (_jsx("div", { className: styles.roleDropdownMenu, children: ROLE_OPTIONS.map((option) => (_jsx("button", { type: "button", className: styles.roleDropdownItem, onClick: () => handleRoleChange(option.value), children: option.label }, option.value))) }))] })] })] }), _jsx("div", { className: styles.sectionDivider }), _jsxs("div", { className: styles.accessSection, children: [_jsx("h3", { className: styles.accessTitle, children: "Access" }), _jsxs("div", { className: styles.accessGroup, children: [_jsx("div", { className: styles.accessGroupTitle, children: "Core Access" }), _jsx("div", { className: styles.accessList, children: CORE_ACCESS_ITEMS.map((item) => (_jsxs("div", { className: styles.accessItem, children: [_jsx("span", { className: styles.accessLabel, children: item.label }), _jsxs("label", { className: styles.toggle, children: [_jsx("input", { type: "checkbox", checked: access[item.key], onChange: () => handleAccessToggle(item.key), disabled: isAdminRole }), _jsx("span", { className: styles.toggleSlider })] })] }, item.key))) })] }), isAdminRole && (_jsxs("div", { className: styles.accessGroup, children: [_jsx("div", { className: styles.accessGroupTitle, children: "Admin Access Menu" }), _jsx("div", { className: styles.accessList, children: ADDITIONAL_ACCESS_ITEMS.map((item) => (_jsxs("div", { className: styles.accessItem, children: [_jsx("span", { className: styles.accessLabel, children: item.label }), _jsxs("label", { className: styles.toggle, children: [_jsx("input", { type: "checkbox", checked: access[item.key], onChange: () => handleAccessToggle(item.key) }), _jsx("span", { className: styles.toggleSlider })] })] }, item.key))) })] }))] })] }), _jsxs("div", { className: styles.modalFooter, children: [_jsx("button", { type: "button", className: styles.cancelBtn, onClick: handleClose, disabled: isSaving, children: "Cancel" }), _jsx("button", { type: "button", className: styles.saveBtn, onClick: handleSave, disabled: !canAddUser || isSaving, children: isSaving ? "Saving..." : "Add User" })] })] }) }));
};
